// IndemnityComptablelist.js (corrigé avec pagination fonctionnelle)

import React, { useEffect, useState } from 'react';
import MainContainer from '../../../components/MainContainer';
import { makeStyles } from '@material-ui/core';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Typography,
  CircularProgress,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid
} from '@mui/material';
import Title from '../../../components/Title';
import IndemnitySettingService from '../../../services/IndemnitySettingService';
import { headerComptable } from '../../../data/modules/comptable-header';
import { DownloadOutlined, SearchOutlined, ClearOutlined } from "@mui/icons-material";
import { useNavigate } from 'react-router-dom';
import IndemnityTrameDialog from './IndemnityTrameDialog';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    elevation: 2,
    borderRadius: '8px',
    marginTop: '2%',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  searchContainer: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
  },
  filterButton: {
    height: '40px',
    width: '100%'
  },
  clearButton: {
    height: '40px',
    width: '100%'
  }
}));

const IndemnityComptablelist = () => {
  const navigate = useNavigate();
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [selectedMission, setSelectedMission] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5); // Changé à 5 par défaut
  const [totalRecords, setTotalRecords] = useState(0);

  // Filtre state
  const [searchReference, setSearchReference] = useState('');
  const [tempSearchReference, setTempSearchReference] = useState('');

  const handleDownloadTrame = (filePath) => {
    if (filePath) {
      const baseUrl = process.env.REACT_APP_API_URL_FILEPGI;
      const fullUrl = `${baseUrl}/${filePath}`;
      
      const link = document.createElement('a');
      link.href = fullUrl;
      link.download = filePath.split('/').pop();
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Fonction pour charger les données avec pagination et filtre
  const loadData = async (pageNum, limit, reference = '') => {
    setLoading(true);
    try {
      // La page de l'API commence à 1
      const result = await IndemnitySettingService.listetrame(pageNum + 1, limit, reference);
      
      if (result && result.status) {
        const enrichedData = result.data.map((mission) => ({
          ...mission,
          missionary: mission.missionary,
        }));
        
        setRows(enrichedData);
        setTotalRecords(result.pagination.total_records);
      } else {
        setRows([]);
        setTotalRecords(0);
        if (result && result.message) {
          console.error(result.message);
        }
      }
    } catch (error) {
      console.error("Erreur lors du chargement des données:", error);
      setRows([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // Appliquer le filtre et rechercher
  const handleSearch = () => {
    setSearchReference(tempSearchReference);
    setPage(0); 
    loadData(0, rowsPerPage, tempSearchReference);
  };

  // Réinitialiser le filtre
  const handleClearFilter = () => {
    setTempSearchReference('');
    setSearchReference('');
    setPage(0);
    loadData(0, rowsPerPage, '');
  };

  // Recherche avec la touche Entrée
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Effet pour charger les données quand la page ou rowsPerPage change
  useEffect(() => {
    loadData(page, rowsPerPage, searchReference);
  }, [page, rowsPerPage, searchReference]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Gestionnaire pour le changement de lignes par page dans le Select
  const handleDisplayedRowsChange = (event) => {
    const newValue = parseInt(event.target.value, 10);
    setRowsPerPage(newValue);
    setPage(0); // Reset à la première page
    // Recharger immédiatement avec la nouvelle limite
    loadData(0, newValue, searchReference);
  };

  // Gestionnaire pour le changement de lignes par page dans TablePagination
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // Recharger immédiatement avec la nouvelle limite
    loadData(0, newRowsPerPage, searchReference);
  };

  const handleOpenTrameDialog = (mission) => {
    setSelectedMission(mission);
    setDialogOpen(true);
  };

  const handleCloseTrameDialog = () => {
    setDialogOpen(false);
    setSelectedMission(null);
    loadData(page, rowsPerPage, searchReference);
  };

  // Affichage du chargement initial
  if (loading && rows.length === 0) {
    return (
      <MainContainer {...headerComptable}>
        <Paper className={classes.paper} variant="outlined">
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        </Paper>
      </MainContainer>
    );
  }

  return (
    <MainContainer {...headerComptable}>
      <Paper className={classes.paper} variant="outlined">
        {/* Barre de recherche et filtres */}
        <Grid container spacing={2} className={classes.searchContainer}>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Rechercher par référence..."
              value={tempSearchReference}
              onChange={(e) => setTempSearchReference(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Lignes par page</InputLabel>
              <Select
                value={rowsPerPage}
                onChange={handleDisplayedRowsChange}
                label="Lignes par page"
              >
                <MenuItem value={5}>5 lignes</MenuItem>
                <MenuItem value={10}>10 lignes</MenuItem>
                <MenuItem value={20}>20 lignes</MenuItem>
                <MenuItem value={50}>50 lignes</MenuItem>
                <MenuItem value={100}>100 lignes</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              className={classes.filterButton}
              startIcon={<SearchOutlined />}
            >
              Filtrer
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              onClick={handleClearFilter}
              className={classes.clearButton}
              startIcon={<ClearOutlined />}
            >
              Effacer
            </Button>
          </Grid>
        </Grid>

        <Title>Liste des missions</Title>

        {/* Affichage du nombre total d'enregistrements */}
        {totalRecords > 0 && (
          <Typography variant="body2" style={{ marginBottom: '10px', color: '#666' }}>
            Total: {totalRecords} mission(s) trouvée(s) - Affichage: {rows.length} mission(s)
          </Typography>
        )}

        {/* Tableau */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Référence</strong></TableCell>
                <TableCell><strong>Objet de la mission</strong></TableCell>
                <TableCell><strong>Lieu</strong></TableCell>
                <TableCell><strong>Date de départ</strong></TableCell>
                <TableCell><strong>Date de retour</strong></TableCell>
                <TableCell><strong>Missionaire(s)</strong></TableCell>
                <TableCell><strong>Rapport de mission</strong></TableCell>
                <TableCell><strong>Fichier trame</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : rows.length > 0 ? (
                rows.map((row) => (
                  <TableRow key={row.mission_planning_id} hover>
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {row.reference}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.mission_title}</TableCell>
                    <TableCell>{row.lieufinal}</TableCell>
                    <TableCell>{row.date_depart}</TableCell>
                    <TableCell>{row.date_fin}</TableCell>
                    <TableCell>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {row.missionary && row.missionary.length > 0 ? (
                          row.missionary.map((ts, index) => (
                            <li key={index} style={{ marginBottom: 4 }}>
                              <strong>{ts.nom}</strong>
                            </li>
                          ))
                        ) : (
                          <li>Aucun missionnaire</li>
                        )}
                      </ul>
                    </TableCell>
                    <TableCell> 
                      <Button 
                        title="Télécharger le rapport de mission"
                        size="small"
                        style={{ marginLeft: 8, minWidth: 'auto', padding: '2px 4px' }}
                      >
                        <DownloadOutlined style={{ color: "#1a5fa1", fontSize: 32 }} />
                      </Button>
                    </TableCell>
                    <TableCell>
                      {row.path && row.path !== "" ? (
                        <Button 
                          title="Télécharger la trame"
                          size="small"
                          style={{ marginLeft: 8, minWidth: 'auto', padding: '2px 4px' }}
                          onClick={() => handleDownloadTrame(row.path)}
                        >
                          <DownloadOutlined style={{ color: "#1a5fa1", fontSize: 32 }} />
                        </Button>
                      ) : (
                        <Typography variant="body2" style={{ color: '#999', fontStyle: 'italic' }}>
                          Aucun fichier trame
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="outlined" 
                        size="medium"
                        onClick={() => handleOpenTrameDialog(row)}
                        style={{ textTransform: 'none' }}
                      >
                        Élaborer trame
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body1" style={{ padding: '20px' }}>
                      Aucune donnée disponible
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination en bas */}
        {totalRecords > 0 && (
          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
            labelRowsPerPage="Lignes par page :"
            showFirstButton
            showLastButton
          />
        )}
      </Paper>

      {/* Dialogue pour la trame comptable */}
      <IndemnityTrameDialog
        open={dialogOpen}
        onClose={handleCloseTrameDialog}
        missionData={selectedMission}
      />
    </MainContainer>
  );
};

export default IndemnityComptablelist;